import React, { useEffect, useState } from "react";
import { toEmbed } from "../lib/video";
import { useParams, Link } from "react-router-dom";
import { MapPin, Maximize, TrendingUp, Building2, Check, Sprout, ArrowLeft } from "lucide-react";
import { getOne } from "../lib/api";
import { normalizeMedia } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { displayPrice, useUsdTry } from "../lib/price";
import { SmartImage } from "../components/SmartImage";
import { SEO } from "../components/SEO";
import { badgeText } from "../lib/badges";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/motion/Reveal";
import { CTASection } from "../components/home/FinancialBenefits";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { t, tc, lang } = useLang();
  const rate = useUsdTry();
  const [project, setProject] = useState(null);

  useEffect(() => {
    getOne("projects", slug).then(setProject).catch(() => setProject(false));
  }, [slug]);

  if (project === null) return <div className="pt-40 pb-40 text-center text-charcoal/50" data-testid="project-loading">{t.common.loading}</div>;
  if (project === false) return <div className="pt-40 pb-40 text-center text-charcoal/50" data-testid="project-not-found">{t.common.projectNotFound}</div>;

  const lifestyle = tc(project.lifestyle_features) || [];
  const agri = tc(project.agricultural_options) || [];
  const gallery = (project.gallery?.length ? project.gallery : [project.image])
    .map(normalizeMedia)
    .filter((m) => m && m.url);

  // Title deed facts filled in by the purchasing team in the accounting panel.
  const land = project.land_details || {};
  const landRows = [
    ["province", project.province], ["district", project.district], ["neighborhood", project.neighborhood],
    ...["block", "parcel_no", "sheet", "deed_type", "land_use", "zoning", "road_access", "electricity", "water"].map((k) => [k, land[k]]),
  ].filter(([, v]) => v);

  const facts = [
    { icon: TrendingUp, label: t.projects.price, value: displayPrice(project.price, project.price_try, lang, rate) || "—" },
    { icon: MapPin, label: t.projects.location, value: project.location },
    { icon: Maximize, label: t.projects.landSize, value: project.land_size },
    { icon: Building2, label: t.projects.type, value: tc(project.project_type) },
  ];

  return (
    <>
      <SEO title={tc(project.title)} description={tc(project.description)} image={project.image_variants?.og?.url || project.image} type="article" />
      <PageHeader overline={project.location} title={tc(project.title)} sub={tc(project.project_type)} image={project.image_variants?.desktop?.url || project.image} mobileImage={project.image_variants?.mobile?.url} />

      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center gap-2 text-charcoal/60 hover:text-green transition-colors mb-10 text-sm">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t.projects.allProjects}
          </Link>

          {/* Facts */}
          <Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
              {facts.map((f, i) => (
                <div key={i} className="bg-lightgray rounded-2xl p-6" data-testid={`project-fact-${i}`}>
                  <f.icon className="w-5 h-5 text-gold mb-3" />
                  <div className="text-charcoal/45 text-xs mb-1">{f.label}</div>
                  <div className="font-display text-lg text-charcoal leading-tight">{f.value}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Overview + features */}
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <Reveal>
                <h2 className="font-display text-3xl text-charcoal mb-4">{t.projects.overview}</h2>
                <p className="text-charcoal/65 leading-relaxed text-lg">{tc(project.description)}</p>
              </Reveal>

              {landRows.length > 0 && (
                <Reveal>
                  <div className="mt-10" data-testid="project-land-details">
                    <h3 className="font-display text-xl text-charcoal mb-4">{t.land.title}</h3>
                    <dl className="grid sm:grid-cols-2 gap-x-8 rounded-2xl bg-lightgray px-5 py-1">
                      {landRows.map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-4 py-3 border-b border-charcoal/10">
                          <dt className="text-charcoal/50 text-sm">{t.land[k]}</dt>
                          <dd className="text-charcoal font-medium text-sm text-end">{t.land.values[v] || v}</dd>
                        </div>
                      ))}
                    </dl>
                    {project.lat != null && project.lng != null && (
                      <a href={`https://www.google.com/maps?q=${project.lat},${project.lng}`} target="_blank" rel="noreferrer" data-testid="project-map-link"
                        className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-green hover:underline">
                        <MapPin className="w-4 h-4" /> {t.land.map}
                      </a>
                    )}
                  </div>
                </Reveal>
              )}

              {(lifestyle.length > 0 || agri.length > 0) && <div className="grid sm:grid-cols-2 gap-8 mt-12">
                <Reveal>
                  <h3 className="font-display text-xl text-charcoal mb-4 flex items-center gap-2"><Building2 className="w-5 h-5 text-green" /> {t.projects.lifestyle}</h3>
                  <ul className="space-y-3">
                    {(lifestyle || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-charcoal/70 text-sm"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {f}</li>
                    ))}
                  </ul>
                </Reveal>
                <Reveal delay={0.1}>
                  <h3 className="font-display text-xl text-charcoal mb-4 flex items-center gap-2"><Sprout className="w-5 h-5 text-green" /> {t.projects.agricultural}</h3>
                  <ul className="space-y-3">
                    {(agri || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-charcoal/70 text-sm"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {f}</li>
                    ))}
                  </ul>
                </Reveal>
              </div>}
            </div>

            <Reveal delay={0.15}>
              <div className="bg-green rounded-3xl p-8 text-white sticky top-28 grain">
                {badgeText(project.badge, lang) && (
                  <span data-testid="project-detail-badge" className="inline-block rounded-full bg-gold text-charcoal text-xs font-bold px-3 py-1.5 mb-4 shadow">{badgeText(project.badge, lang)}</span>
                )}
                <span className="overline">{t.projects.status}</span>
                <div className="font-display text-2xl mt-2 mb-6">{t.status[project.status] || project.status}</div>
                <p className="text-white/70 text-sm mb-6">{t.projects.relatedNote}</p>
                <Link to="/contact" data-testid="project-cta" className="btn-shine block text-center rounded-full bg-gold text-charcoal font-semibold py-3.5 hover:bg-gold-soft transition-colors">
                  {t.cta.book}
                </Link>
              </div>
            </Reveal>
          </div>

          {toEmbed(project.video_url) && (
            <Reveal delay={0.1}>
              <div className="mt-20 rounded-3xl overflow-hidden aspect-video bg-charcoal" data-testid="project-video">
                <iframe src={toEmbed(project.video_url)} title={tc(project.title)} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
              </div>
            </Reveal>
          )}

          {/* Gallery */}
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl text-charcoal mt-20 mb-8">{t.projects.gallery}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map((m, i) => (
                <div key={i} className="rounded-2xl overflow-hidden hover-zoom aspect-[4/3] bg-charcoal/5" data-testid={`gallery-img-${i}`}>
                  {m.type === "video" ? (
                    <video src={m.url} controls playsInline className="w-full h-full object-cover" />
                  ) : (
                    <SmartImage src={m.variants?.card?.url || m.url} alt={`${tc(project.title)} ${i + 1}`} className="w-full h-full bg-charcoal" />
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
      <CTASection />
    </>
  );
}
